import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CRON_REGISTRY, isJobLate, type CronJobDef } from '@/lib/admin/cron-registry';

interface CronInvocationRow {
  out_function: string;
  out_last_run_at: string | null;
  out_last_status: string | null;
  out_runs_24h: number;
  out_failures_24h: number;
}

export interface CronJobStatus {
  job: CronJobDef;
  lastRunAt: Date | null;
  lastStatus: string | null;
  runs24h: number;
  failures24h: number;
  late: boolean;
  overdueMinutes: number;
  neverRun: boolean;
}

export interface CronHealthSummary {
  ok: number;
  late: number;
  failed: number;
}

async function fetchCronHealth(): Promise<{ jobs: CronJobStatus[]; summary: CronHealthSummary }> {
  const { data, error } = await supabase.rpc('admin_cron_health');
  if (error) throw error;

  const rows = (data as CronInvocationRow[]) ?? [];
  const byFunction = new Map<string, CronInvocationRow>();
  for (const row of rows) {
    // Multiple registry entries may map to the same function (e.g., reconcile-orchestrator stagger runs).
    // Store all of them so we can look up per function.
    byFunction.set(row.out_function, row);
  }

  const jobs: CronJobStatus[] = CRON_REGISTRY.map((job) => {
    const row = byFunction.get(job.functionName);
    const lastRunAt = row?.out_last_run_at ? new Date(row.out_last_run_at) : null;
    const lateStatus = isJobLate(job, lastRunAt);

    return {
      job,
      lastRunAt,
      lastStatus: row?.out_last_status ?? null,
      runs24h: row?.out_runs_24h ?? 0,
      failures24h: row?.out_failures_24h ?? 0,
      late: lateStatus.late,
      overdueMinutes: lateStatus.overdueMinutes,
      neverRun: !row,
    };
  });

  const summary: CronHealthSummary = {
    ok: jobs.filter((j) => !j.late && !j.neverRun && j.failures24h === 0).length,
    late: jobs.filter((j) => j.late || j.neverRun).length,
    failed: jobs.filter((j) => j.failures24h > 0).length,
  };

  return { jobs, summary };
}

export function useAdminCronHealth(enabled: boolean, refetchInterval: number | false) {
  return useQuery({
    queryKey: ['admin-cron-health'],
    queryFn: fetchCronHealth,
    enabled,
    refetchInterval,
  });
}
